import Cookies from 'js-cookie'
import LogTool from '../logger/logTools'
import { HTTPMethod } from './types'
import { refreshAccessToken } from './authTokens'

const log = new LogTool({ context: 'APIClient', enable: true, logLevel: 'warn' })

/**
 * API Error class for structured error handling
 */
export class APIError extends Error {
  status: number
  statusText: string
  data?: any

  constructor(status: number, statusText: string, data?: any) {
    super(`API Error ${status}: ${statusText}`)
    this.name = 'APIError'
    this.status = status
    this.statusText = statusText
    this.data = data
  }
}

/**
 * Request options for API calls
 */
export interface RequestOptions {
  method: HTTPMethod
  headers?: Record<string, string>
  body?: any
  returnType?: 'json' | 'blob' | 'text'
  isFile?: boolean
}

/**
 * Detail response from the API
 */
export type DetailResponse<T> = T

/**
 * Paginated response from the API
 */
export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

/**
 * Central API client for making requests to the backend
 * Handles authentication, token refresh, and error handling
 */
export class APIClient {
  private baseURL: string

  constructor() {
    this.baseURL = this.resolveBaseURL()
  }

  /**
   * Resolve the API base URL from the environment.
   */
  private resolveBaseURL(): string {
    const envApiUrl = process.env.REACT_APP_API_URL
    if (!envApiUrl) {
      throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
    }

    return envApiUrl.endsWith('/') ? envApiUrl : `${envApiUrl}/`
  }

  /**
   * Return the current base URL, reloading from the environment when it changes.
   */
  private getBaseURL(): string {
    const normalized = this.resolveBaseURL()
    if (this.baseURL !== normalized) {
      this.baseURL = normalized
    }
    return this.baseURL
  }

  /**
   * Build absolute URL from relative path
   */
  private buildURL(url: string): string {
    const baseURL = this.getBaseURL()

    // If URL is already absolute, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }

    // If URL starts with /v1/, remove leading slash if baseURL ends with slash
    if (url.startsWith('/v1/')) {
      return baseURL.endsWith('/')
        ? baseURL.slice(0, -1) + url
        : baseURL + url
    }

    // Otherwise append to baseURL
    return baseURL + url
  }

  /**
   * Make an API request with automatic token refresh on 401
   */
  async request<T = any>(
    url: string,
    options: RequestOptions
  ): Promise<T> {
    const { method, headers = {}, body, returnType = 'json', isFile = false } = options

    // Prepare headers
    const requestHeaders: Record<string, string> = { ...headers }

    // Add auth token if available
    const accessToken = Cookies.get('access')
    if (accessToken && !requestHeaders.Authorization) {
      requestHeaders.Authorization = `Bearer ${accessToken}`
    }

    // Set content type for non-file requests
    if (!requestHeaders['Content-Type'] && !isFile) {
      requestHeaders['Content-Type'] = 'application/json'
    }

    // Prepare request parameters
    const requestParams: RequestInit = {
      method,
      headers: requestHeaders,
    }

    // Add body if present
    if (body !== undefined) {
      requestParams.body = isFile ? body : JSON.stringify(body)
    }

    // Build absolute URL
    const absoluteURL = this.buildURL(url)

    try {
      // Make the request
      let response = await fetch(absoluteURL, requestParams)

      // Handle 401 (Unauthorized) - try to refresh token
      if (response.status === 401) {
        log.info('Received 401, attempting to refresh token...')
        const newToken = await refreshAccessToken()

        if (!newToken) {
          throw new APIError(401, 'Unauthorized', 'Token refresh failed')
        }

        // Update authorization header and retry request
        requestHeaders.Authorization = `Bearer ${newToken}`
        requestParams.headers = requestHeaders

        response = await fetch(absoluteURL, requestParams)

        if (!response.ok) {
          throw new APIError(response.status, response.statusText)
        }
      } else if (response.status === 403) {
        throw new APIError(403, 'Forbidden')
      } else if (!response.ok) {
        // Try to get error details from response
        let errorData
        try {
          errorData = await response.json()
        } catch {
          errorData = await response.text()
        }
        throw new APIError(response.status, response.statusText, errorData)
      }

      // Parse response based on return type
      switch (returnType) {
        case 'json':
          return await response.json()
        case 'blob':
          return await response.blob() as T
        case 'text':
          return await response.text() as T
        default:
          return await response.json()
      }
    } catch (error) {
      if (error instanceof APIError) {
        throw error
      }

      if (error && typeof error === 'object') {
        const possibleApiError = error as {
          name?: string
          status?: number
          statusText?: string
          message?: string
          data?: unknown
        }

        if (possibleApiError.name === 'APIError' || typeof possibleApiError.status === 'number') {
          const status = possibleApiError.status ?? 0
          const statusText = possibleApiError.statusText ?? possibleApiError.message ?? ''
          throw new APIError(status, statusText, possibleApiError.data)
        }
      }

      log.error('API request failed:', error)
      throw new Error('Network error or server unavailable')
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, { method: HTTPMethod.GET, headers })
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    body?: any,
    headers?: Record<string, string>,
    isFile = false
  ): Promise<T> {
    return this.request<T>(url, { method: HTTPMethod.POST, body, headers, isFile })
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    body?: any,
    headers?: Record<string, string>,
    isFile = false
  ): Promise<T> {
    return this.request<T>(url, { method: HTTPMethod.PUT, body, headers, isFile })
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(url, { method: HTTPMethod.PATCH, body, headers })
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    url: string,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(url, {
      method: HTTPMethod.DELETE,
      headers,
      returnType: 'text'
    })
  }

  /**
   * Download a blob (file)
   */
  async downloadBlob(url: string, headers?: Record<string, string>): Promise<Blob> {
    return this.request<Blob>(url, {
      method: HTTPMethod.GET,
      headers,
      returnType: 'blob'
    })
  }
}

// Export singleton instance
export const apiClient = new APIClient()
